import Staging, { URLS } from "containers/Staging";
import Intro from "containers/Staging/Intro";
import CharacterInfo from "containers/Staging/CharacterInfo";
import NewDesigns from "containers/Staging/NewDesigns";
import Login from "containers/Staging/Login";
import StartGame from "containers/Staging/StartGame";
import EndGame from "containers/Staging/EndGame";
import {
  Home,
  DesktopHome,
  Options,
  Credit,
  Settings,
  Rules,
  PlayGame,
} from "pages";
import BackgroundImage from "components/BackgroundImage";
import SessionNameModal from "components/SessionNameModal";
import InvisibleMusicPlayer from "components/InvisibleMusicPlayer";
import { actions } from "core/constants";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { getLocation, setLocation } from "reducer/location";
import { SESSION_NAME, getOption, setOption } from "reducer/options";
import { useMusicVolume } from "hooks/sound";

const { VALIDATE_SESSION } = actions;

const Routes = ({ connected, socket }) => {
  const dispatch = useDispatch();
  const location = useLocation(); // Use React Router's useLocation hook directly
  const locationRef = useSelector(getLocation);
  const sessionNameFromState = useSelector(getOption(SESSION_NAME));
  const [sessionError, setSessionError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const musicVolume = useMusicVolume();

  // Check if we're using desktop mode (path starts with /desktop)
  const isDesktopMode = location.pathname.startsWith("/desktop");

  // Initialize Redux location state on mount
  useEffect(() => {
    if (!locationRef || !locationRef.pathname) {
      dispatch(setLocation(location));
    }
  }, [dispatch, location, locationRef]);

  const validateSessionName = useCallback(
    (sessionName) => {
      console.log("🔍 validateSessionName called:", {
        sessionName,
        connected,
        isDesktopMode,
      });
      if (connected && sessionName) {
        setIsValidating(true);
        console.log("📡 Emitting VALIDATE_SESSION:", { sessionName });
        socket.emit(
          VALIDATE_SESSION,
          {
            sessionName,
          },
          ({ status, error }) => {
            console.log("📨 VALIDATE_SESSION response:", {
              status,
              error,
              isDesktopMode,
            });
            setIsValidating(false);
            // For desktop mode: accept 404 (new session) or 200 (existing joinable session)
            // For mobile mode: only accept 200 (existing session in waiting/ready state)
            if (status === 200 || (isDesktopMode && status === 404)) {
              console.log("✅ Session name accepted");
              setSessionError(""); // Clear any previous errors
              dispatch(setOption(SESSION_NAME, sessionName));
            } else if (status === 403) {
              // Game already in progress
              console.log("❌ Game in progress");
              dispatch(setOption(SESSION_NAME, ""));
              setSessionError(error);
            } else {
              console.log("❌ Session validation failed");
              dispatch(setOption(SESSION_NAME, ""));
              setSessionError(error);
            }
          }
        );
      } else {
        console.log("⚠️ Skipping validation:", {
          connected,
          hasSessionName: !!sessionName,
        });
      }
    },
    [connected, dispatch, socket, isDesktopMode]
  );

  useEffect(() => {
    // Auto-login desktop with existing session from localStorage
    if (isDesktopMode && !sessionNameFromState && connected) {
      const existingSessionId = localStorage.getItem("Game Session Id");
      if (existingSessionId) {
        console.log("🔄 Auto-login with existing session:", existingSessionId);
        validateSessionName(existingSessionId);
        return;
      }
    }

    // Only validate on initial load or when there's an error
    // Don't re-validate after successfully accepting a session name
    const shouldShowModal = !sessionNameFromState || !!sessionError;
    if (shouldShowModal && sessionNameFromState) {
      validateSessionName(sessionNameFromState);
    }
  }, [
    sessionNameFromState,
    validateSessionName,
    sessionError,
    isDesktopMode,
    connected,
  ]);

  return (
    <>
      {/* Only show SessionNameModal, BackgroundImage and Music on desktop routes */}
      {isDesktopMode && (
        <>
          <InvisibleMusicPlayer
            url="audio/Vampire Party Main Menu.wav"
            playing
            loop
            volume={musicVolume}
          />
          <SessionNameModal
            showModal={!sessionNameFromState || !!sessionError}
            onEnterSessionName={validateSessionName}
            isLoading={isValidating}
            error={sessionError}
          />
          <BackgroundImage />
        </>
      )}
      <Switch location={locationRef} key={locationRef.path}>
        {/* Mobile/Staging routes (original) - only show if NOT in desktop mode */}
        {!isDesktopMode && (
          <>
            <Route exact path="/">
              <Home loading={!connected} socket={socket} />
            </Route>
            <Route exact path="/staging" component={Staging} />
            <Route exact path={URLS.VOTING} component={NewDesigns} />
            <Route exact path={URLS.INTRO} component={Intro} />
            <Route exact path={URLS.LOGIN} component={Login} />
            <Route exact path={URLS.START_GAME} component={StartGame} />
            <Route exact path={URLS.END_GAME} component={EndGame} />
            <Route exact path={URLS.CHARACTER_INFO} component={CharacterInfo} />
            <Redirect from="*" to="/" />
          </>
        )}

        {/* Desktop/Electron app routes - only show if in desktop mode */}
        {isDesktopMode && (
          <>
            <Route exact path="/" component={DesktopHome} />
            <Route exact path="/desktop" component={DesktopHome} />
            <Route path="/Options" component={Options} />
            <Route path="/desktop/Options" component={Options} />
            <Route path="/Credit" component={Credit} />
            <Route path="/desktop/Credit" component={Credit} />
            <Route path="/Settings" component={Settings} />
            <Route path="/desktop/Settings" component={Settings} />
            <Route path="/Rules" component={Rules} />
            <Route path="/desktop/Rules" component={Rules} />
            <Route path="/PlayGame">
              <PlayGame socket={socket} connected={connected} />
            </Route>
            <Route path="/desktop/PlayGame">
              <PlayGame socket={socket} connected={connected} />
            </Route>
          </>
        )}
      </Switch>
    </>
  );
};
export default Routes;
